<template>
  <div class="card p-5">
    <div v-if="alert" role="alert" class="alert cirx-alert success-alert elevated alert-dismissible fade show mx-5">
      {{ alertMessage }}
      <button type="button" class="close" data-dismiss="alert" aria-label="Close">
        <span aria-hidden="true">&times;</span>
      </button>
    </div>

    <InCardLoader v-if="isLoading" :loadingMessage="translate('SHARED.LOADING')" />

    <div v-else class="card-body p-0 px-5 mx-5">
      <ProductsFilters v-model="filters" @filter="filterProducts" />

      <div class="my-5">
        <InCardLoader v-if="isLoadingProducts" :loadingMessage="translate('SHARED.LOADING')" />

        <table v-else class="table">
          <thead>
            <tr>
              <th
                v-for="field in fields"
                :key="field.key"
                :class="{
                  'cursor-pointer': field.sortable,
                  'text-muted': filters.sort.field !== field.key,
                  'cirx-custom-primary-color': filters.sort.field === field.key,
                }"
                :nowrap="field.nowrap"
                @click="field.sortable ? sortByColumn(field) : null"
              >
                <inline-svg v-if="field.sortable" width="13" height="13" :src="getSortImg(field.key)" />
                {{ field.label }}
              </th>
            </tr>
          </thead>

          <tbody>
            <tr v-if="!products.length">
              <td colspan="7" class="pt-5 text-center font-weight-bold">
                {{ translate("PRODUCT.NO_MATCH") }}
              </td>
            </tr>
            <ProductsRow v-for="(product, key) in products" :key="key" :product="product" />
          </tbody>
        </table>
      </div>

      <Placeholder v-if="totalItems === null" style="height: 36px" />
      <TablePagination
        v-else-if="totalItems > 0"
        :totalItems="totalItems"
        :currentPage="currentPage"
        :currentPerPage="perPage"
        :totalPages="totalPages"
        :perPageOptions="pageOptions"
        :ressourceName="translate('PRODUCT.TITLE')"
        :nbOfItemsDisplayed="nbOfProductsDisplayed"
        @newPage="onPaginationChange"
        @newPerPage="onPerPageChange"
      />
    </div>
  </div>
</template>

<script setup>
/* eslint no-unused-vars:off */
import { computed, onMounted, ref } from "vue";
import Placeholder from "@/view/components/Shared/Placeholder.vue";
import TablePagination from "@/view/components/Shared/TablePagination.vue";
import InCardLoader from "@/view/components/Shared/InCardLoader.vue";
import ProductsRow from "@/apps/dashboard/catalog/view/components/Product/ProductsRow.vue";
import ProductsFilters from "@/apps/dashboard/catalog/view/components/Product/ProductsFilters.vue";
import { getProductsList } from "@/apps/dashboard/catalog/core/infrastructure/service/productsService";
import { HeaderAction } from "@/core/store/headerButton/headerAction";
import { useRouter } from "vue-router";
import { routes } from "@/routing/routes";
import { getFilters } from "@/apps/dashboard/catalog/core/infrastructure/service/productFiltersService";
import useTranslate from "@/core/composables/translate";
import useBreadcrumbStore from "@/core/store/config/breadcrumbsStore";
import useHeaderButtonStore from "@/core/store/headerButton/headerButtonStore";

const router = useRouter();
const breadcrumbStore = useBreadcrumbStore();
const { translate } = useTranslate();

const { alert } = defineProps({
  alert: Object,
});
const { initHeaderAction } = useHeaderButtonStore();
const filters = ref({});
const currentPage = ref(1);
const perPage = ref(10);
const totalItems = ref(null);
const isLoadingProducts = ref(true);
const products = ref([]);
const isLoading = ref(true);
const pageOptions = [5, 10, 15, 30];

const nbOfProductsDisplayed = computed(() =>
  totalItems.value ? Math.min(currentPage.value * perPage.value, totalItems.value) : 0
);
const totalPages = computed(() => (totalItems.value ? Math.ceil(totalItems.value / perPage.value) : 1));

const alertMessage = computed(() => {
  if (!alert) {
    return;
  }

  let message = "";

  const { type, productName, status } = alert;

  if (type === "create") {
    message += translate("PRODUCT.ALERT.CREATE_SUCCESS", { productName });
  } else if (type === "edit") {
    message += translate("PRODUCT.ALERT.EDIT_SUCCESS", { productName });
  }

  if (status) {
    message += `${translate("PRODUCT.ALERT.STATUS_PRODUCT", { status })}`;
  }

  return message;
});

onMounted(() => {
  filters.value = getFilters();
  fetchProducts(filters.value).finally(() => (isLoading.value = false));

  initHeaderAction(
    new HeaderAction(translate("PRODUCT.CREATE"), () => router.push({ name: routes.createProduct.name }))
  );

  breadcrumbStore.setBreadcrumb([{ title: translate("PRODUCT.TITLE"), bc_icon: translate("PRODUCT.ICON") }]);
});

const fetchProducts = async (newFilters) => {
  isLoadingProducts.value = true;
  totalItems.value = null;

  newFilters.sort = filters.value.sort;
  newFilters.page = currentPage.value;
  newFilters.itemsPerPage = perPage.value;
  filters.value = newFilters;

  try {
    const res = await getProductsList(filters.value);
    products.value = res.items;
    totalItems.value = res.totalItems;
  } finally {
    isLoadingProducts.value = false;
  }
};

const filterProducts = (newFilters) => {
  currentPage.value = 1;

  fetchProducts(newFilters);
};

const sortByColumn = (field) => {
  filters.value.page = 1;
  filters.value.sort = {
    field: field.key,
    order: filters.value.sort?.order === "asc" ? "desc" : "asc",
  };

  fetchProducts(filters.value);
};

const onPaginationChange = (page) => {
  currentPage.value = page;

  fetchProducts(filters.value);
};

const onPerPageChange = (newPerPage) => {
  currentPage.value = 1;
  perPage.value = newPerPage;

  fetchProducts(filters.value);
};

const getSortImg = (fieldKey) => {
  let imgName = "sortable";
  if (filters.value.sort?.field === fieldKey) {
    imgName = filters.value.sort?.order === "asc" ? "sorted_asc" : "sorted_desc";
  }

  return `/media/cirx/${imgName}.svg`;
};

const fields = [
  {
    key: "label",
    label: translate("PRODUCT.LIST_HEADERS.NAME"),
    sortable: true,
    nowrap: true,
  },
  {
    key: "eanCode",
    label: translate("PRODUCT.LIST_HEADERS.EAN_NUMBER"),
    sortable: false,
    nowrap: false,
  },
  {
    key: "brand",
    label: translate("PRODUCT.LIST_HEADERS.BRAND"),
    sortable: true,
    nowrap: true,
  },
  {
    key: "category.label",
    label: translate("PRODUCT.LIST_HEADERS.CATEGORY"),
    sortable: true,
  },
  {
    key: "buybackPriceGradeA",
    label: translate("PRODUCT.LIST_HEADERS.PRICE"),
    sortable: true,
    nowrap: false,
  },
  {
    key: "status",
    label: translate("PRODUCT.LIST_HEADERS.STATUS"),
    sortable: true,
    nowrap: true,
  },
  {
    key: "action",
    label: translate("PRODUCT.LIST_HEADERS.ACTION"),
    nowrap: true,
  },
];
</script>
